import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, addDays, addWeeks, subWeeks, addMonths, subMonths,
         startOfMonth, endOfMonth, isToday, isSameMonth } from 'date-fns';
import { useMealPlan } from '../hooks/useMealPlan';
import useWindowSize from '../hooks/useWindowSize';
import MenuItemDetail from './MenuItemDetail';
import MonthDayMeals from './MonthDayMeals';
import MealPlanifyModal from './MealPlanifyModal';
import IngredientVerificationModal from './IngredientVerificationModal';
import ShoppingListModal from './ShoppingListModal';
import './MealPlan.css';
import InstacartService from '../services/instacartService';
import cartService from '../services/cartService';
import { toast } from 'react-hot-toast';
import { useWeightUnit } from '../hooks/useWeightUnit';
import { accumulateIngredient, finalizeAggregatedItem, formatConvertedAmount, fromGrams } from '../utils/weightUnits';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const MealPlan = () => {
  const weightUnit = useWeightUnit();
  const navigate = useNavigate();
  const { mealPlan, loading, error, updateMeal, removeMeal } = useMealPlan();
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isMealPlanifyOpen, setIsMealPlanifyOpen] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [aggregatedIngredients, setAggregatedIngredients] = useState([]);
  const [sourceRecipes, setSourceRecipes] = useState([]);
  const [shareUrl, setShareUrl] = useState(null);
  // showShareModal removed - sharing is now inline via mp-share-strip
  const [shareLoading, setShareLoading] = useState(false);
  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);
  const [shoppingListIngredients, setShoppingListIngredients] = useState([]);
  const [shoppingListRecipes, setShoppingListRecipes] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);

  // Calendar state
  const [calendarView, setCalendarView] = useState('week');
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [monthDate, setMonthDate] = useState(new Date());
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const monthPillLimit = isMobile ? 2 : 3;

  // Fetch current nutrition plan on mount
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/nutrition/current-plan`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setCurrentPlan(data);
        }
      } catch (err) {
        console.error('Error fetching current plan:', err);
      }
    };
    
    fetchCurrentPlan();
  }, []);

  // Helper: get meals for a specific day
  const getMealsForDay = useCallback((day) => {
    if (!mealPlan?.meals) return [];
    const dayStr = format(day, 'yyyy-MM-dd');
    return mealPlan.meals.filter(meal => {
      const mealDate = new Date(meal.date);
      return format(mealDate, 'yyyy-MM-dd') === dayStr;
    });
  }, [mealPlan]);

  // Handle clicking a meal card
  const handleMealClick = useCallback((meal) => {
    if (isSelectMode) {
      const mealDate = new Date(meal.date);
      const dateStr = format(mealDate, 'yyyy-MM-dd');
      setSelectedDates(prev => {
        const exists = prev.some(d => format(d, 'yyyy-MM-dd') === dateStr);
        if (exists) return prev.filter(d => format(d, 'yyyy-MM-dd') !== dateStr);
        return [...prev, mealDate];
      });
    } else {
      if (meal.isFood) return; // Foods don't have a detail view
      if (meal.menuItemId) setSelectedMenuItem(meal.menuItemId);
    }
  }, [isSelectMode]);

  // Handle clicking a day header (for select mode)
  const handleDayClick = useCallback((day) => {
    if (!isSelectMode) return;
    const dateStr = format(day, 'yyyy-MM-dd');
    setSelectedDates(prev => {
      const exists = prev.some(d => format(d, 'yyyy-MM-dd') === dateStr);
      if (exists) return prev.filter(d => format(d, 'yyyy-MM-dd') !== dateStr);
      return [...prev, day];
    });
  }, [isSelectMode]);

  const isDateSelected = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return selectedDates.some(d => format(d, 'yyyy-MM-dd') === dateStr);
  }, [selectedDates]);

  // Week navigation
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);

  // Month grid: build 6-row grid of days
  const getMonthGrid = () => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    // Start from Monday of the week containing monthStart
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const rows = [];
    let day = gridStart;
    for (let row = 0; row < 6; row++) {
      const week = [];
      for (let col = 0; col < 7; col++) {
        week.push(day);
        day = addDays(day, 1);
      }
      // Only include row if at least one day is in the current month
      if (week.some(d => isSameMonth(d, monthDate))) {
        rows.push(week);
      }
    }
    return rows;
  };

  // Meal type icon and accent color
  const getMealTypeStyle = (mealType) => {
    switch (mealType) {
      case 'breakfast': return { icon: 'fa-sun', color: '#f59e0b' };
      case 'lunch': return { icon: 'fa-cloud-sun', color: '#3b82f6' };
      case 'dinner': return { icon: 'fa-moon', color: '#8b5cf6' };
      case 'snack': return { icon: 'fa-cookie-bite', color: '#10b981' };
      default: return { icon: 'fa-utensils', color: '#8b5cf6' };
    }
  };

  const handleBuyIngredients = async () => {
    try {
      setIsLoading(true);
      
      // Only process meals from selected dates
      const mealsToProcess = mealPlan?.meals.filter(meal => {
        const mealDate = new Date(meal.date);
        return selectedDates.some(selectedDate => 
          mealDate.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0]
        );
      });

      if (!mealsToProcess || mealsToProcess.length === 0) {
        toast.error('No meals found for the selected dates');
        return;
      }

      // Get title for shopping list
      const dateRange = `Selected Days (${selectedDates.length} days)`;
      
      // Get all menu item IDs, ensuring they are valid ObjectIds
      const menuItemIds = mealsToProcess
        .filter(meal => meal.menuItemId && meal.menuItemId._id)
        .map(meal => meal.menuItemId._id);

      // Check if we have any valid items (menu items OR food items)
      const foodItemsCount = mealsToProcess.filter(meal => meal.isFood).length;
      if (menuItemIds.length === 0 && foodItemsCount === 0) {
        toast.error('No valid menu items or foods found');
        return;
      }
      
      // Create ingredient map for menu items
      let preferences = {};
      
      // Fetch ingredient preferences for menu items (if any)
      if (menuItemIds.length > 0) {
        const preferencesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/preferences/batch/preferences`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ menuItemIds })
          }
        );
        
        if (preferencesResponse.ok) {
          const data = await preferencesResponse.json();
          preferences = data.preferences || {};
        }
      }
      
      // Create a map to store aggregated ingredients
      const ingredientMap = new Map();

      // Process each menu item meal and aggregate ingredients
      mealsToProcess.forEach(meal => {
        if (!meal.menuItemId || !meal.menuItemId._id) return;
        
        const menuItemId = meal.menuItemId._id;
        const menuItemPrefs = preferences[menuItemId] || {};
        const selectedIndices = menuItemPrefs.selectedIngredients || 
          meal.menuItemId.ingredients.map((_, index) => index);
        
        const preferredServings = menuItemPrefs.preferredServings || meal.menuItemId.servings;
        const scaleFactor = preferredServings / meal.menuItemId.servings;

        selectedIndices.forEach(index => {
          const ingredient = meal.menuItemId.ingredients[index];
          if (!ingredient) return;

          if (typeof ingredient === 'string') {
            // For string ingredients, just add them as is
            ingredientMap.set(ingredient, ingredient);
          } else {
            accumulateIngredient(
              ingredientMap,
              ingredient.name,
              (ingredient.quantity || 0) * scaleFactor,
              ingredient.unit
            );
          }
        });
      });

      // Convert the map values back to an array
      const aggregatedIngredients = Array.from(ingredientMap.values()).map((item) => (
        typeof item === 'string' ? item : finalizeAggregatedItem(item, weightUnit)
      ));

      // Also add food items directly to the shopping list
      const foodItems = mealsToProcess.filter(meal => meal.isFood);
      foodItems.forEach(foodMeal => {
        // Food data is stored in meal.food object
        const foodData = foodMeal.food || {};
        const foodName = foodData.description || 'Food item';
        const quantity = foodData.quantity || 1;
        const key = foodName.toLowerCase();
        
        if (aggregatedIngredients.some(ing => typeof ing === 'string' ? ing.toLowerCase() === key : ing.name?.toLowerCase() === key)) {
          // Already exists, add to quantity
          const existingIdx = aggregatedIngredients.findIndex(ing => 
            typeof ing === 'string' ? ing.toLowerCase() === key : ing.name?.toLowerCase() === key
          );
          if (existingIdx >= 0 && typeof aggregatedIngredients[existingIdx] !== 'string') {
            aggregatedIngredients[existingIdx].quantity += quantity;
          }
        } else {
          aggregatedIngredients.push({
            name: foodName,
            quantity: quantity,
            unit: foodData.servingSize ? `${foodData.servingSize}g` : 'serving',
            source: 'food',
            brandOwner: foodData.brandOwner
          });
        }
      });

      if (aggregatedIngredients.length === 0) {
        toast.error('No ingredients selected for the chosen meals');
        return;
      }
      
      // Collect source recipes for the verification modal (only menu items, not food items)
      const menuMeals = mealsToProcess.filter(meal => meal.menuItemId && meal.menuItemId._id);
      const sourceRecipes = menuMeals.map(meal => ({
        title: meal.menuItemId.title,
        image: meal.menuItemId.image,
        ingredients: meal.menuItemId.ingredients
      }));
      
      // Set the data for the verification modal
      setAggregatedIngredients(aggregatedIngredients);
      setSourceRecipes(sourceRecipes);
      setIsVerificationModalOpen(true);
      
    } catch (error) {
      console.error('Error adding ingredients:', error);
      toast.error('Failed to add ingredients to cart');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmIngredients = async (filteredIngredients) => {
    try {
      setIsLoading(true);
      
      const instacart = new InstacartService();
      const dateRange = `Selected Days (${selectedDates.length} days)`;
      
      const instacartLink = await instacart.createShoppingList(
        filteredIngredients,
        `Meal Plan - ${dateRange}`,
        window.location.href, // menuItemUrl - use current page URL
        null // menuItemId - null for meal plan (not a single menu item)
      );
      
      window.open(instacartLink, '_blank', 'noopener,noreferrer');
      toast.success('Ingredients added to your Instacart!');
      
      // Close modal and clear selections
      setIsVerificationModalOpen(false);
      setSelectedDates([]);
      setIsSelectMode(false);
      
    } catch (error) {
      console.error('Error adding ingredients to Instacart:', error);
      toast.error('Failed to add ingredients to cart');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSelectedToCart = async () => {
    try {
      setIsLoading(true);
      
      // Get meals from selected dates
      const mealsToProcess = mealPlan?.meals.filter(meal => {
        const mealDate = new Date(meal.date).toISOString().split('T')[0];
        return selectedDates.some(selectedDate => {
          const selected = new Date(selectedDate).toISOString().split('T')[0];
          return selected === mealDate;
        });
      });

      if (!mealsToProcess || mealsToProcess.length === 0) {
        toast.error('No meals found for the selected dates');
        return;
      }

      let addedMenus = 0;
      let addedFoods = 0;

      // One add per meal slot so the same recipe on two days/slots becomes 2× cart count
      const menuMeals = mealsToProcess.filter(meal => !meal.isFood && meal.menuItemId && meal.menuItemId._id);
      for (const meal of menuMeals) {
        const menuId = meal.menuItemId._id;
        try {
          await cartService.addMenuToCart(menuId);
          addedMenus++;
        } catch (err) {
          console.error(`Failed to add ${menuId} to cart:`, err);
        }
      }

      // Collect individual food items and aggregate by description
      const foodMeals = mealsToProcess.filter(meal => meal.isFood && meal.food);
      const foodAggregates = {};
      foodMeals.forEach(meal => {
        const food = meal.food;
        const description = food.description || 'Food item';
        const totalGrams = (food.servingSize || 100) * (food.quantity || 1);
        
        if (foodAggregates[description]) {
          foodAggregates[description].totalGrams += totalGrams;
        } else {
          foodAggregates[description] = {
            description,
            totalGrams,
            brandOwner: food.brandOwner
          };
        }
      });

      // Add aggregated foods as custom items to the cart
      for (const food of Object.values(foodAggregates)) {
        try {
          const mass = fromGrams(food.totalGrams, weightUnit);
          const quantity = formatConvertedAmount(mass.quantity, mass.unit);
          const unit = mass.unit;
          const name = food.brandOwner 
            ? `${food.description} (${food.brandOwner})`
            : food.description;
          await cartService.addCustomItem(name, quantity, unit);
          addedFoods++;
        } catch (err) {
          console.error(`Failed to add food "${food.description}" to cart:`, err);
        }
      }

      const totalAdded = addedMenus + addedFoods;
      if (totalAdded > 0) {
        const parts = [];
        if (addedMenus > 0) parts.push(`${addedMenus} recipe${addedMenus > 1 ? 's' : ''}`);
        if (addedFoods > 0) parts.push(`${addedFoods} food${addedFoods > 1 ? 's' : ''}`);
        toast.success(`Added ${parts.join(' and ')} to shopping list!`);
        setSelectedDates([]);
        setIsSelectMode(false);
        navigate('/cart');
      } else {
        toast.error('No items found to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to shopping list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenShoppingList = async () => {
    try {
      setIsLoading(true);
      
      // Only process meals from selected dates
      const mealsToProcess = mealPlan?.meals.filter(meal => {
        const mealDate = new Date(meal.date);
        return selectedDates.some(selectedDate => 
          mealDate.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0]
        );
      });

      if (!mealsToProcess || mealsToProcess.length === 0) {
        toast.error('No meals found for the selected dates');
        return;
      }

      // Get all menu item IDs (for recipe meals only)
      const menuItemIds = mealsToProcess
        .filter(meal => meal.menuItemId && meal.menuItemId._id)
        .map(meal => meal.menuItemId._id);

      // Create a map to store aggregated ingredients
      const ingredientMap = new Map();
      let preferences = {};

      // Fetch ingredient preferences if we have menu items
      if (menuItemIds.length > 0) {
        const preferencesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/preferences/batch/preferences`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ menuItemIds })
          }
        );
        
        if (preferencesResponse.ok) {
          const data = await preferencesResponse.json();
          preferences = data.preferences || {};
        }
      }

      // Process menu items and aggregate ingredients
      mealsToProcess.forEach(meal => {
        if (!meal.menuItemId || !meal.menuItemId._id) return;
        
        const menuItemId = meal.menuItemId._id;
        const menuItemPrefs = preferences[menuItemId] || {};
        const selectedIndices = menuItemPrefs.selectedIngredients || 
          meal.menuItemId.ingredients.map((_, index) => index);
        
        const preferredServings = menuItemPrefs.preferredServings || meal.menuItemId.servings;
        const scaleFactor = preferredServings / meal.menuItemId.servings;

        selectedIndices.forEach(index => {
          const ingredient = meal.menuItemId.ingredients[index];
          if (!ingredient) return;

          if (typeof ingredient === 'string') {
            ingredientMap.set(ingredient, ingredient);
          } else {
            accumulateIngredient(
              ingredientMap,
              ingredient.name,
              (ingredient.quantity || 0) * scaleFactor,
              ingredient.unit
            );
          }
        });
      });

      const aggregatedIngredients = Array.from(ingredientMap.values()).map((item) => (
        typeof item === 'string' ? item : finalizeAggregatedItem(item, weightUnit)
      ));

      // Also add food items directly to the shopping list
      const foodItems = mealsToProcess.filter(meal => meal.isFood);
      foodItems.forEach(foodMeal => {
        const foodData = foodMeal.food || {};
        const foodName = foodData.description || 'Food item';
        const quantity = foodData.quantity || 1;
        const key = foodName.toLowerCase();
        
        const existingIdx = aggregatedIngredients.findIndex(ing => 
          typeof ing === 'string' ? ing.toLowerCase() === key : ing.name?.toLowerCase() === key
        );
        
        if (existingIdx >= 0 && typeof aggregatedIngredients[existingIdx] !== 'string') {
          aggregatedIngredients[existingIdx].quantity += quantity;
        } else if (existingIdx < 0) {
          aggregatedIngredients.push({
            name: foodName,
            quantity: quantity,
            unit: foodData.servingSize ? `${foodData.servingSize}g` : 'serving',
            source: 'food',
            brandOwner: foodData.brandOwner
          });
        }
      });

      if (aggregatedIngredients.length === 0) {
        toast.error('No ingredients or foods found for the selected meals');
        return;
      }
      
      // Collect source recipes (only menu items, not food items)
      const sourceRecipes = mealsToProcess
        .filter(meal => meal.menuItemId && meal.menuItemId._id)
        .map(meal => ({
          title: meal.menuItemId.title,
          image: meal.menuItemId.image,
          ingredients: meal.menuItemId.ingredients
        }));

      // Add food items as their own "recipe" source for display
      const foodSourceItems = foodItems.map(foodMeal => ({
        title: foodMeal.food?.description || 'Food Item',
        image: null,
        ingredients: [{
          name: foodMeal.food?.description || 'Food Item',
          quantity: foodMeal.food?.quantity || 1,
          unit: foodMeal.food?.servingSize ? `${foodMeal.food?.servingSize}g` : 'serving'
        }],
        isFood: true,
        brandOwner: foodMeal.food?.brandOwner
      }));
      
      setShoppingListIngredients(aggregatedIngredients);
      setShoppingListRecipes([...sourceRecipes, ...foodSourceItems]);
      setIsShoppingListOpen(true);
      
    } catch (error) {
      console.error('Error generating shopping list:', error);
      toast.error('Failed to generate shopping list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMealPlanGenerated = useCallback(() => {
    window.location.reload();
  }, []);

  const handleShareClick = async () => {
    try {
      setShareLoading(true);
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      
      if (!user) {
        toast.error('You must be logged in to share your meal plan');
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/mealplan/${user.sub}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate share link');
      }

      const data = await response.json();
      setShareUrl(data.shareUrl);
      toast.success('Sharing enabled! Copy the link below.');
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleDisableSharing = async () => {
    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      
      if (!user) return;

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/mealplan/${user.sub}/share`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to disable sharing');
      }

      setShareUrl(null);
      toast.success('Sharing disabled');
    } catch (error) {
      console.error('Error disabling sharing:', error);
      toast.error('Failed to disable sharing');
    }
  };

  if (loading) return <div className="mp-loading"><i className="fas fa-spinner fa-spin"></i><p>Loading meal plan...</p></div>;
  if (error) return <div className="mp-error">Error: {error}</div>;

  // Render a single meal card
  const renderMealCard = (meal) => {
    const style = getMealTypeStyle(meal.mealType);
    const isFood = meal.isFood && meal.food;
    const menuItem = meal.menuItemId;

    if (isFood) {
      const food = meal.food;
      const scaleFactor = (food.servingSize || 100) / 100 * (food.quantity || 1);
      const calories = Math.round((food.nutrients?.calories || 0) * scaleFactor);
      const protein = Math.round((food.nutrients?.protein || 0) * scaleFactor * 10) / 10;

      return (
        <div
          key={meal._id}
          className={`mp-meal-card ${isSelectMode ? 'select-mode' : ''}`}
          style={{ borderLeftColor: style.color }}
          onClick={() => handleMealClick(meal)}
        >
          <div className="mp-meal-header">
            <i className={`fas ${style.icon}`} style={{ color: style.color }}></i>
            <span className="mp-meal-type">{meal.mealType}</span>
          </div>
          <div className="mp-meal-body">
            <div className="mp-meal-icon food">
              <i className="fas fa-leaf"></i>
            </div>
            <div className="mp-meal-info">
              <h4 className="mp-meal-name" title={food.description}>
                {food.description?.length > 28 ? food.description.substring(0, 28) + '…' : food.description}
              </h4>
              <div className="mp-meal-macros">
                <span>{calories} cal</span>
                <span>{protein}g P</span>
              </div>
            </div>
          </div>
          <button
            className="mp-remove-btn"
            onClick={(e) => { e.stopPropagation(); removeMeal(meal._id); toast.success('Food removed'); }}
            title="Remove"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      );
    }

    if (!menuItem) {
      return (
        <div key={meal._id} className="mp-meal-card error" style={{ borderLeftColor: '#ef4444' }}>
          <div className="mp-meal-header">
            <i className={`fas ${style.icon}`} style={{ color: style.color }}></i>
            <span className="mp-meal-type">{meal.mealType}</span>
          </div>
          <div className="mp-meal-body">
            <div className="mp-meal-info"><h4 className="mp-meal-name">Item not found</h4></div>
          </div>
          <button
            className="mp-remove-btn"
            onClick={(e) => { e.stopPropagation(); removeMeal(meal._id); toast.success('Removed'); }}
            title="Remove"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      );
    }

    return (
      <div
        key={meal._id}
        className={`mp-meal-card ${isSelectMode ? 'select-mode' : ''}`}
        style={{ borderLeftColor: style.color }}
        onClick={() => handleMealClick(meal)}
      >
        <div className="mp-meal-header">
          <i className={`fas ${style.icon}`} style={{ color: style.color }}></i>
          <span className="mp-meal-type">{meal.mealType}</span>
        </div>
        <div className="mp-meal-body">
          {menuItem.image && (
            <img src={menuItem.image} alt={menuItem.title} className="mp-meal-image" />
          )}
          <div className="mp-meal-info">
            <h4 className="mp-meal-name">{menuItem.title}</h4>
            {menuItem.nutrition?.calories && (
              <div className="mp-meal-macros">
                <span>{Math.round(menuItem.nutrition.calories)} cal</span>
              </div>
            )}
          </div>
        </div>
        <button
          className="mp-remove-btn"
          onClick={(e) => { e.stopPropagation(); removeMeal(meal._id); toast.success(`${menuItem.title} removed`); }}
          title="Remove"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>
    );
  };

  return (
    <div className="mp-page">
      {/* Decision fork banner */}
      <div className="meal-plan-decision-banner">
        <div
          className="meal-plan-decision-option link"
          onClick={() => setIsMealPlanifyOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && setIsMealPlanifyOpen(true)}
        >
          <span className="decision-icon">🟢</span>
          <div>
            <strong>Simple Meal Planning</strong>
            <p>Generate meals for the week based on your preferences</p>
          </div>
          <span className="decision-arrow">→</span>
        </div>
        <div className="meal-plan-decision-divider">or</div>
        <div
          className="meal-plan-decision-option link"
          onClick={() => navigate(currentPlan?.id ? `/nutrition/planner?goalId=${currentPlan.id}` : '/nutrition/planner')}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && navigate(currentPlan?.id ? `/nutrition/planner?goalId=${currentPlan.id}` : '/nutrition/planner')}
        >
          <span className="decision-icon">🔵</span>
          <div>
            <strong>Nutrition Planner</strong>
            <p>Control calories, macros &amp; protein targets</p>
          </div>
          <span className="decision-arrow">→</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mp-actions">
        <button
          className={`mp-action-btn planify${isMealPlanifyOpen ? ' active' : ''}`}
          onClick={() => setIsMealPlanifyOpen(prev => !prev)}
        >
          <i className="fas fa-magic"></i> MealPlanify
        </button>
        <button
          className={`mp-action-btn share${shareUrl ? ' active' : ''}`}
          onClick={shareUrl ? handleDisableSharing : handleShareClick}
          disabled={shareLoading}
        >
          <i className={`fas ${shareUrl ? 'fa-stop' : 'fa-share-alt'}`}></i>
          {shareLoading ? 'Loading...' : shareUrl ? 'Stop Sharing' : 'Share Plan'}
        </button>
        <button
          className={`mp-action-btn select ${isSelectMode ? 'active' : ''}`}
          onClick={() => { setIsSelectMode(!isSelectMode); if (!isSelectMode) setSelectedDates([]); }}
        >
          <i className="fas fa-calendar-check"></i>
          {isSelectMode ? 'Exit Selection' : 'Select Dates'}
        </button>
        {isSelectMode && selectedDates.length > 0 && (
          <button
            className="mp-action-btn cart"
            onClick={handleAddSelectedToCart}
            disabled={isLoading}
          >
            <i className="fas fa-cart-plus"></i>
            {isLoading ? 'Adding...' : `Add to Cart (${selectedDates.length} day${selectedDates.length > 1 ? 's' : ''})`}
          </button>
        )}
      </div>

      {shareUrl && (
        <div className="mp-share-strip">
          <i className="fas fa-share-alt mp-share-strip-icon"></i>
          <span className="mp-share-strip-label">Sharing enabled</span>
          <input type="text" value={shareUrl} readOnly className="mp-share-strip-input" onClick={(e) => e.target.select()} />
          <button className="mp-share-strip-copy" onClick={handleCopyShareLink}>
            <i className="fas fa-copy"></i> Copy
          </button>
          <button className="mp-share-strip-stop" onClick={handleDisableSharing}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {isSelectMode && (
        <div className="mp-select-banner">
          <i className="fas fa-info-circle"></i>
          {selectedDates.length === 0
            ? 'Tap on days or meal cards to select dates for your shopping cart'
            : `${selectedDates.length} day${selectedDates.length > 1 ? 's' : ''} selected`}
        </div>
      )}

      {isMealPlanifyOpen && (
        <MealPlanifyModal
          isOpen={isMealPlanifyOpen}
          inline={true}
          onClose={() => setIsMealPlanifyOpen(false)}
          onMealPlanGenerated={handleMealPlanGenerated}
        />
      )}

      {/* Calendar navigator */}
      <div className="mp-calendar-nav">
        <button
          className="mp-nav-btn"
          onClick={() => calendarView === 'week'
            ? setWeekStart(subWeeks(weekStart, 1))
            : setMonthDate(subMonths(monthDate, 1))}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        <div className="mp-nav-label">
          {calendarView === 'week'
            ? <span className="mp-nav-dates">{format(weekStart, 'MMM d')} — {format(weekEnd, 'MMM d, yyyy')}</span>
            : <span className="mp-nav-dates">{format(monthDate, 'MMMM yyyy')}</span>}
        </div>
        <button
          className="mp-nav-btn"
          onClick={() => calendarView === 'week'
            ? setWeekStart(addWeeks(weekStart, 1))
            : setMonthDate(addMonths(monthDate, 1))}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
        <button
          className="mp-today-btn"
          onClick={() => {
            setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
            setMonthDate(new Date());
          }}
        >
          Today
        </button>
        <div className="mp-view-toggle">
          <button
            className={`mp-view-btn ${calendarView === 'week' ? 'active' : ''}`}
            onClick={() => setCalendarView('week')}
          >
            Week
          </button>
          <button
            className={`mp-view-btn ${calendarView === 'month' ? 'active' : ''}`}
            onClick={() => setCalendarView('month')}
          >
            Month
          </button>
        </div>
      </div>

      {/* Week View */}
      {calendarView === 'week' && (
        <div className="mp-week-grid">
          {weekDays.map(day => {
            const dayMeals = getMealsForDay(day);
            const today = isToday(day);
            const selected = isDateSelected(day);
            return (
              <div
                key={day.toISOString()}
                className={`mp-day-col ${today ? 'today' : ''} ${selected ? 'selected' : ''}`}
              >
                <div
                  className="mp-day-header"
                  onClick={() => handleDayClick(day)}
                  role={isSelectMode ? 'button' : undefined}
                >
                  <span className="mp-day-name">{format(day, 'EEE')}</span>
                  <span className={`mp-day-date ${today ? 'today-accent' : ''}`}>{format(day, 'd')}</span>
                </div>
                <div className="mp-day-items">
                  {dayMeals.length === 0 ? (
                    <div className="mp-day-empty">No meals</div>
                  ) : (
                    dayMeals.map(meal => renderMealCard(meal))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Month View */}
      {calendarView === 'month' && (
        <div className="mp-month-grid">
          <div className="mp-month-header-row">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} className="mp-month-header-cell">{d}</div>
            ))}
          </div>
          {getMonthGrid().map((week, wi) => (
            <div key={wi} className="mp-month-week-row">
              {week.map(day => {
                const dayMeals = getMealsForDay(day);
                const today = isToday(day);
                const inMonth = isSameMonth(day, monthDate);
                const selected = isDateSelected(day);
                return (
                  <div
                    key={day.toISOString()}
                    className={`mp-month-cell ${today ? 'today' : ''} ${!inMonth ? 'outside' : ''} ${selected ? 'selected' : ''} ${dayMeals.length ? 'has-meals' : ''}`}
                    onClick={() => handleDayClick(day)}
                  >
                    <span className={`mp-month-day-num ${today ? 'today-accent' : ''}`}>{format(day, 'd')}</span>
                    <MonthDayMeals
                      meals={dayMeals}
                      maxPills={monthPillLimit}
                      getMealName={(meal) => meal.isFood
                        ? (meal.food?.description || 'Food')
                        : (meal.menuItemId?.title || 'Item')}
                      onMealClick={handleMealClick}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedMenuItem && !isSelectMode && (
        <MenuItemDetail
          item={selectedMenuItem}
          onClose={() => setSelectedMenuItem(null)}
        />
      )}

      <IngredientVerificationModal
        isOpen={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        onConfirm={handleConfirmIngredients}
        aggregatedIngredients={aggregatedIngredients}
        sourceRecipes={sourceRecipes}
        isLoading={isLoading}
      />

      <ShoppingListModal
        isOpen={isShoppingListOpen}
        onClose={() => setIsShoppingListOpen(false)}
        aggregatedIngredients={shoppingListIngredients}
        sourceRecipes={shoppingListRecipes}
        selectedDatesCount={selectedDates.length}
      />

      {/* Share modal removed - replaced by inline mp-share-strip above */}
    </div>
  );
};

export default MealPlan;